import React, { useEffect, useState } from "react";
import { validateLoggedIn, getSession } from "../utils";
import {
    TextField, Paper, CircularProgress, Typography, Box, Grid, Button, Alert, MenuItem,
} from "@mui/material";
import NavBar from "../navbar";
import { useNavigate, useParams } from "react-router-dom";
import config from "../config";
import * as Sentry from "@sentry/react";

const EditRestriction = () => {
    const { restriction_id } = useParams();
    const navigate = useNavigate();

    const [data, setData] = useState(null);
    const [originalData, setOriginalData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(config.apiBaseUrl + `/admin/endpoint-restrictions`, {
                    headers: { "Content-Type": "application/json", "user-authorization": getSession().token },
                });
                if (!response.ok) throw new Error(`Error: ${response.status}`);
                const all = await response.json();
                const found = all.find(r => r.id === restriction_id);
                if (!found) throw new Error("Restriction not found");
                setData(found);
                setOriginalData(found);
            } catch (err) {
                Sentry.captureException(err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [restriction_id]);

    const validate_logged_in = validateLoggedIn();
    if (validate_logged_in) return validate_logged_in;

    const handleChange = (field, value) => setData({ ...data, [field]: value });

    const handleSave = async () => {
        try {
            setSaving(true); setError(null);
            const payload = {
                id: data.id,
                endpoint_id: data.endpoint_id,
                plan_id: data.plan_id,
                ten_second_limit: data.ten_second_limit !== "" ? data.ten_second_limit : null,
                minutely_limit: data.minutely_limit !== "" ? data.minutely_limit : null,
                hourly_limit: data.hourly_limit !== "" ? data.hourly_limit : null,
                daily_limit: data.daily_limit !== "" ? data.daily_limit : null,
                monthly_limit: data.monthly_limit !== "" ? data.monthly_limit : null,
                total_limit: data.total_limit !== "" ? data.total_limit : null,
                price: parseInt(data.price),
                max_results: data.max_results !== null && data.max_results !== "" ? parseInt(data.max_results) : null,
                increase_every: data.increase_every !== null && data.increase_every !== "" ? parseInt(data.increase_every) : null,
                disabled: data.disabled,
            };
            const response = await fetch(config.apiBaseUrl + "/admin/endpoint-restrictions", {
                method: "PUT",
                headers: { "Content-Type": "application/json", "user-authorization": getSession().token },
                body: JSON.stringify(payload),
            });
            if (!response.ok) { const err = await response.json(); throw new Error(err.detail || `Error: ${response.status}`); }
            setSuccess(true);
            setOriginalData(await response.json());
            setTimeout(() => setSuccess(false), 3000);
        } catch (err) {
            Sentry.captureException(err); setError(err.message);
        } finally { setSaving(false); }
    };

    const handleDelete = async () => {
        if (!window.confirm("Delete this restriction?")) return;
        try {
            const response = await fetch(config.apiBaseUrl + `/admin/endpoint-restrictions?restriction_id=${restriction_id}`, {
                method: "DELETE",
                headers: { "Content-Type": "application/json", "user-authorization": getSession().token },
            });
            if (!response.ok) throw new Error(`Error: ${response.status}`);
            alert("Deleted!"); navigate("/endpoint-restrictions");
        } catch (err) { Sentry.captureException(err); alert(`Failed: ${err.message}`); }
    };

    const hasChanges = data && originalData && JSON.stringify(data) !== JSON.stringify(originalData);
    if (loading) return <div style={{ textAlign: "center", padding: "20px" }}><CircularProgress /></div>;
    if (error && !data) return <div style={{ textAlign: "center", padding: "20px", color: "red" }}><p>{error}</p></div>;

    const limitField = (label, field) => (
        <Grid item xs={12} md={4}>
            <TextField fullWidth margin="normal" label={label} type="number" inputProps={{ min: 1 }}
                value={data?.[field] === null ? "" : data?.[field]}
                onChange={(e) => handleChange(field, e.target.value === "" ? null : parseInt(e.target.value) || null)}
                disabled={saving} helperText="Empty = no limit" />
        </Grid>
    );

    return (
        <>
            <NavBar />
            <Box sx={{ maxWidth: 900, margin: "auto", padding: 3 }}>
                <Paper sx={{ padding: 3 }}>
                    <Typography variant="h5" gutterBottom>Edit Endpoint Restriction</Typography>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    {success && <Alert severity="success" sx={{ mb: 2 }}>Updated successfully!</Alert>}
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField fullWidth margin="normal" label="Endpoint ID" value={data?.endpoint_id || ""} onChange={(e) => handleChange("endpoint_id", e.target.value)} disabled={saving} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField fullWidth margin="normal" label="Plan ID" value={data?.plan_id || ""} onChange={(e) => handleChange("plan_id", e.target.value)} disabled={saving} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField fullWidth margin="normal" label="Price" type="number" inputProps={{ min: 1 }} value={data?.price || 1} onChange={(e) => handleChange("price", parseInt(e.target.value) || 1)} disabled={saving} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField fullWidth margin="normal" label="Max Results" type="number" value={data?.max_results === null ? "" : data?.max_results} onChange={(e) => handleChange("max_results", e.target.value === "" ? null : parseInt(e.target.value) || null)} disabled={saving} helperText="Empty = no limit" />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField fullWidth margin="normal" label="Increase Every" type="number" inputProps={{ min: 1 }} value={data?.increase_every === null ? "" : data?.increase_every} onChange={(e) => handleChange("increase_every", e.target.value === "" ? null : parseInt(e.target.value) || null)} disabled={saving} helperText="Empty = no increase" />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField select fullWidth margin="normal" label="Disabled" value={data?.disabled ? "true" : "false"} onChange={(e) => handleChange("disabled", e.target.value === "true")} disabled={saving}>
                                <MenuItem value="false">No</MenuItem>
                                <MenuItem value="true">Yes</MenuItem>
                            </TextField>
                        </Grid>
                        {limitField("10s Limit", "ten_second_limit")}
                        {limitField("Minutely Limit", "minutely_limit")}
                        {limitField("Hourly Limit", "hourly_limit")}
                        {limitField("Daily Limit", "daily_limit")}
                        {limitField("Monthly Limit", "monthly_limit")}
                        {limitField("Total Limit", "total_limit")}
                    </Grid>
                    <Box sx={{ display: "flex", justifyContent: "space-between", marginTop: 3 }}>
                        <Button variant="contained" color="primary" onClick={handleSave} disabled={saving || !hasChanges}>{saving ? "Saving..." : "Save Changes"}</Button>
                        <Box>
                            <Button variant="outlined" color="secondary" onClick={() => setData(originalData)} disabled={saving || !hasChanges} sx={{ mr: 2 }}>Discard</Button>
                            <Button variant="outlined" color="error" onClick={handleDelete} disabled={saving} sx={{ mr: 2 }}>Delete</Button>
                            <Button variant="outlined" onClick={() => navigate("/endpoint-restrictions")} disabled={saving}>Back</Button>
                        </Box>
                    </Box>
                </Paper>
            </Box>
        </>
    );
};

export default EditRestriction;
